import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient, type MutateOptions, type UseMutationResult } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { readEffectiveOrganizationId, useOrganization } from "./useOrganization";
import { useAuth } from "./useAuth";
import { logAudit } from "@/lib/audit";
import { toast } from "sonner";
import type { AppRole } from "./useUserRoles";
import { DEFAULT_MESSAGING_ROLE_SCOPES, type MessagingAudience } from "@/lib/messaging-scopes";
import { defaultPeopleStatuses, normalizePeopleStatuses, type PeopleStatusOption } from "@/lib/people-status";
import { isSupabaseUnavailableError } from "@/lib/supabase-compat";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import { cacheConfirmedOrgSettings, getOrgSettingsSaveCoordinator, writeOrgSettingsPatch } from "@/lib/org-settings-save";
import { mergeChmsOrgSettings, parseChmsOrgSettings } from "@/lib/chms-settings";
import { getEdgeFunctionErrorMessage } from "@/lib/edge-function-errors";
import { DEFAULT_PICKUP_SECURITY, type PickupSecurityPolicy } from "@/lib/pickup-security-settings";

export interface OnboardingChurchProfile {
  name?: string;
  address?: string;
  timezone?: string;
  contactEmail?: string;
  contactPhone?: string;
}

export interface OnboardingState {
  currentStep?: string;
  completedAt?: string;
  churchProfile?: OnboardingChurchProfile;
}

export interface OrgWidgetSettings {
  giving?: {
    defaults?: {
      amounts?: number[];
      fundId?: string | null;
      recurring?: "single" | "weekly" | "monthly" | "quarterly" | "annual";
      campaignId?: string | null;
      /**
       * "checkout" was removed (#5923): public-widgets wrote it straight into
       * contributions.method, which violates contributions_method_check and
       * 500'd every gift through the widget. src/types/widget-api.ts records
       * the same removal — a Stripe checkout session maps to card or ach.
       */
      stripeMode?: "card" | "ach" | "other";
      widgetType?: "giving" | "events" | "groups" | "portal" | "form";
    };
    orgTheme?: {
      primaryColor?: string;
      accentColor?: string;
      radiusPx?: number;
    };
  };
}

export interface ImportBatch {
  id: string;
  fileName: string;
  createdAt: string;
  status: "queued" | "processed" | "rolled_back";
  records: number;
  duplicates: number;
  scope: string[];
  rollbackAvailable: boolean;
}

export interface PickupCodeEntry {
  id: string;
  eventId: string;
  personId: string;
  code: string;
  issuedAt: string;
  expiresAt: string;
  usedAt?: string | null;
}

export interface OrgTerminology {
  people: string;
  person: string;
  household: string;
  households: string;
  contribution: string;
  contributions: string;
  fund: string;
  funds: string;
  fundDesignated: string;
  fundRestricted: string;
  member: string;
  members: string;
  visitor: string;
  visitors: string;
}

export interface OrgSettings {
  // NOTE: There is intentionally no nested `security` blob. Org-level MFA and
  // session policy live in flat top-level keys (mfaPolicy, mfaRequiredRoles,
  // mfaReauthPeriodHours, mfaRememberDeviceWindowDays, staffSessionTimeoutHours)
  // that SettingsSecurity owns as the single canonical writer. A legacy
  // `settings.security` shape used to double-surface these and clobber the flat
  // keys on load; it has been removed and is dropped on parse (see chms-settings).
  terminology: OrgTerminology;
  timezone: string; // IANA timezone identifier, e.g. "America/New_York"
  fiscalYearStart: string; // MM-DD format
  defaultContributionMethod: string;
  requireContributionApproval: boolean;
  /** Email the donor a receipt when staff mark a gift reviewed (#6479). Off unless turned on. */
  sendReceiptOnReview?: boolean;
  /**
   * Roles that may cancel a pledge (#7097). Absent means the safe default (admin, finance, finance_admin); the
   * database enforces it (guard_pledge_cancel_roles), and only an organization admin can change it.
   */
  pledgeCancelRoles?: AppRole[];
  titles: string[];
  departments: string[];
  employmentTypes: string[];
  compensationBands: string[];
  householdRelationships: string[];
  genderIdentities: string[];
  maritalStatuses: string[];
  ethnicityCategories: string[];
  preferredLanguages: string[];
  leaveTypes: string[];
  hrRequestCategories: string[];
  assetClassifications: string[];
  assetEquipment: string[];
  assetCampuses: {
    name: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
  }[];
  campaignFundTracking?: Record<
    string,
    {
      enabled?: boolean;
      campaignId?: string | null;
    }
  >;
  archivedFunds?: string[];
  peopleStatuses?: PeopleStatusOption[];
  /** Keep the public giving, join and groups pages light for every visitor (DARK_MODE.md). */
  publicPagesForceLight?: boolean;
  childcare?: {
    checkInAgeRange?: {
      min: number;
      max: number;
    };
    pickupSecurity?: PickupSecurityPolicy;
  };
  onboardingProfile?: {
    size?: "small" | "medium" | "large";
    ministries?: string[];
    serviceTimes?: string[];
    completedAt?: string;
  };
  onboardingState?: OnboardingState;
  onboardingChecklist?: Record<string, Record<string, boolean>>;
  messagingScopes?: Record<string, MessagingAudience>;
  messagingRoleScopes?: Record<AppRole, MessagingAudience[]>;
  savedSegments?: {
    id: string;
    name: string;
    filterKey: string;
    createdAt: string;
  }[];
  importBatches?: ImportBatch[];
  pickupCodes?: Record<string, PickupCodeEntry[]>;
  selfRegistration?: {
    enabled: boolean;
    approvalMode: "manual" | "auto";
  };
  demoMode?: {
    enabled?: boolean;
  };
  mfaPolicy?: "optional" | "required_roles" | "required_staff";
  mfaRequiredRoles?: AppRole[];
  mfaRememberDeviceWindowDays?: 0 | 7 | 30 | 90;
  mfaReauthPeriodHours?: 0 | 12 | 24 | 168 | 720;
  staffSessionTimeoutHours?: 4 | 8 | 12 | 24 | 72 | 168;
  // Days after MFA enforcement is turned on before it becomes mandatory; honored
  // by useMfaRequirement together with mfaEnforcedAt (W6-4).
  mfaGracePeriodDays?: number;
  // ISO timestamp set when mfaPolicy transitions optional → required, used as the
  // grace-period anchor. Null/absent means "no grace" (enforce immediately).
  mfaEnforcedAt?: string | null;
  // Password policy enforced by src/lib/password-policy.ts (W6-4). Absent keys
  // fall back to the permissive baseline (min length 8, no complexity).
  minPasswordLength?: number;
  requireUppercase?: boolean;
  requireNumbers?: boolean;
  requireSpecialChars?: boolean;
  payrollPeriod?: {
    start: string;
    end: string;
  };
  bulletinShareLinksEnabled?: boolean;
  contributionAmountPresets?: number[];
  automationNotifications?: {
    eventRecipients?: string[];
    thresholdRecipients?: string[];
    failureRateThreshold?: number;
    delayMinutesThreshold?: number;
    dailyVolumeThreshold?: number;
  };
  scheduledJobs?: Record<ScheduledJobId, ScheduledJobSchedule>;
  scheduledJobHistory?: ScheduledJobRun[];
  publicGivingKiosk?: {
    enabled?: boolean;
    fullScreenEnabled?: boolean;
    idleResetEnabled?: boolean;
    idleResetSeconds?: number;
  };
  expenseCategories?: {
    id: string;
    name: string;
    code?: string;
    description?: string;
    is_active: boolean;
  }[];
  widgetSettings?: OrgWidgetSettings;
}

export type ScheduledJobId = "cleanup" | "summaries" | "reminders";

export interface ScheduledJobSchedule {
  enabled: boolean;
  cadence: "daily" | "weekly" | "monthly";
  time: string;
  dayOfWeek?: number | null;
  dayOfMonth?: number | null;
}

export interface ScheduledJobRun {
  id: string;
  jobId: ScheduledJobId;
  status: "success" | "failed" | "running";
  startedAt: string;
  finishedAt?: string | null;
  summary?: string | null;
}

const defaultTerminology: OrgTerminology = {
  people: "People",
  person: "Person",
  household: "Household",
  households: "Households",
  contribution: "Contribution",
  contributions: "Contributions",
  fund: "Fund",
  funds: "Funds",
  fundDesignated: "Vestry Designated",
  fundRestricted: "Donor Restricted",
  member: "Member",
  members: "Members",
  visitor: "Visitor",
  visitors: "Visitors",
};

export const defaultOrgOptionLists = {
  titles: [
    "Mr.",
    "Mrs.",
    "Ms.",
    "Miss",
    "Dr.",
    "Rev.",
    "Fr.",
    "Sr.",
    "Br.",
    "Pastor",
    "Bishop",
    "Deacon",
    "Elder",
    "Prof.",
  ],
  departments: ["Worship", "Programs", "Facilities"],
  employmentTypes: ["Full-time", "Part-time", "Contract"],
  compensationBands: ["Band A", "Band B", "Band C", "Band D"],
  householdRelationships: [
    "Head of household",
    "Spouse",
    "Child",
    "Parent",
    "Sibling",
    "Grandparent",
    "Grandchild",
    "Guardian",
    "In-law",
    "Step-parent",
    "Stepchild",
    "Other",
  ],
  genderIdentities: ["Female", "Male", "Non-binary", "Prefer not to say", "Other"],
  maritalStatuses: ["Single", "Married", "Separated", "Divorced", "Widowed"],
  ethnicityCategories: [
    "Hispanic or Latino",
    "Black or African American",
    "Asian",
    "White",
    "Native American",
    "Pacific Islander",
    "Middle Eastern",
    "Multiracial",
    "Other",
    "Prefer not to say",
  ],
  preferredLanguages: ["English", "Spanish", "French", "Mandarin", "Other"],
  leaveTypes: ["Vacation", "Sick", "Personal", "Bereavement", "Jury Duty", "Medical", "Other"],
  hrRequestCategories: [
    "Accommodation",
    "Leave",
    "Compensation",
    "Benefits",
    "Profile update",
    "Grievance",
    "Other",
  ],
  assetClassifications: ["Sanctuary", "Classroom", "Office", "Storage"],
  assetEquipment: ["Projector", "Audio system", "TV", "WiFi", "Piano"],
  assetCampuses: [
    {
      name: "Main Campus",
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      postal_code: "",
    },
  ],
};

const defaultSettings: OrgSettings = {
  terminology: defaultTerminology,
  timezone: "America/New_York",
  fiscalYearStart: "01-01",
  defaultContributionMethod: "check",
  requireContributionApproval: false,
  sendReceiptOnReview: false,
  ...defaultOrgOptionLists,
  peopleStatuses: defaultPeopleStatuses,
  onboardingProfile: {},
  onboardingState: {},
  onboardingChecklist: {},
  messagingScopes: {},
  messagingRoleScopes: DEFAULT_MESSAGING_ROLE_SCOPES,
  savedSegments: [],
  importBatches: [],
  pickupCodes: {},
  selfRegistration: {
    enabled: false,
    approvalMode: "manual",
  },
  demoMode: {
    enabled: false,
  },
  mfaPolicy: "required_roles",
  mfaRequiredRoles: ["admin"],
  mfaRememberDeviceWindowDays: 30,
  mfaReauthPeriodHours: 0,
  campaignFundTracking: {},
  archivedFunds: [],
  childcare: {
    checkInAgeRange: {
      min: 0,
      max: 12,
    },
    pickupSecurity: DEFAULT_PICKUP_SECURITY,
  },
  bulletinShareLinksEnabled: false,
  scheduledJobs: {
    cleanup: {
      enabled: true,
      cadence: "weekly",
      dayOfWeek: 0,
      time: "02:00",
    },
    summaries: {
      enabled: false,
      cadence: "weekly",
      dayOfWeek: 1,
      time: "08:00",
    },
    reminders: {
      enabled: false,
      cadence: "daily",
      time: "09:00",
    },
  },
  scheduledJobHistory: [],
  publicGivingKiosk: {
    enabled: false,
    fullScreenEnabled: true,
    idleResetEnabled: true,
    idleResetSeconds: 120,
  },
  staffSessionTimeoutHours: 12,
};

export function useOrgSettings() {
  const { organization, organizationId, refetch: refetchOrg } = useOrganization();
  const queryClient = useQueryClient();
  const parsedSettings = parseChmsOrgSettings(organization?.settings);
  // True only when the org's actual persisted settings have an
  // assetCampuses entry — false for an org that's never touched this
  // key, where settings.assetCampuses below falls back to
  // defaultSettings' synthesized "Main Campus" placeholder. A consumer
  // that treats that placeholder as real, persisted legacy data (e.g.
  // seeding it into a migrate/remove flow) will find nothing matching it
  // in the actual database and fail every time.
  const hasPersistedAssetCampuses = Boolean((parsedSettings as Partial<OrgSettings>)?.assetCampuses);

  // Same trap, and a costlier one. `defaultSettings.fiscalYearStart` is
  // "01-01", so `settings.fiscalYearStart` is *never* absent — an org that has
  // never set a fiscal year is indistinguishable from one that chose January.
  // The fiscal date filters need that distinction: without it, an
  // organization whose real calendar is unreadable (the `finance_settings`
  // SELECT policy excludes most roles) looks like a January organization, and
  // every fiscal preset silently returns calendar-year money under a fiscal
  // label.
  const hasPersistedFiscalYearStart = Boolean(
    (parsedSettings as Partial<OrgSettings>)?.fiscalYearStart,
  );

  // Deep-merged for the same reason terminology is below: an org that
  // persisted messagingRoleScopes before a role was added to AppRole has no
  // key for it in the stored object, and a plain top-level spread would
  // leave that role missing entirely rather than falling back to
  // DEFAULT_MESSAGING_ROLE_SCOPES's entry -- getAllowedAudiencesForRoles's
  // `scopes[role]?.forEach(...)` then silently resolves that role to zero
  // audiences instead of its intended default.
  //
  // Memoized on a serialized key, not the object reference: parsedSettings
  // is a fresh object every render (parseChmsOrgSettings does no caching of
  // its own), so a plain inline spread here would hand consumers a new
  // messagingRoleScopes reference on every render regardless of whether the
  // persisted content actually changed. SettingsPermissions.tsx has a
  // useEffect keyed on settings.messagingRoleScopes that unconditionally
  // calls setState -- with an unstable reference that fires, sets state,
  // rerenders, computes a new reference, and repeats, hitting React's
  // "Maximum update depth exceeded" (Codex P1 on PR #5905). A serialized
  // dependency key is content-, not reference-, sensitive, so useMemo only
  // produces a new object when the persisted map actually changes.
  const persistedMessagingRoleScopesKey = JSON.stringify(
    (parsedSettings as Partial<OrgSettings>)?.messagingRoleScopes ?? null
  );
  const messagingRoleScopes = useMemo(
    () => ({
      ...DEFAULT_MESSAGING_ROLE_SCOPES,
      ...((parsedSettings as Partial<OrgSettings>)?.messagingRoleScopes || {}),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- persistedMessagingRoleScopesKey IS the content-equality form of parsedSettings?.messagingRoleScopes; depending on the object itself would defeat the memoization this exists for.
    [persistedMessagingRoleScopesKey]
  );

  const settings: OrgSettings = {
    ...defaultSettings,
    ...(parsedSettings as Partial<OrgSettings>),
    terminology: {
      ...defaultTerminology,
      ...((parsedSettings as Partial<OrgSettings>)?.terminology || {}),
    },
    peopleStatuses: normalizePeopleStatuses(
      (parsedSettings as Partial<OrgSettings>)?.peopleStatuses
    ),
    messagingRoleScopes,
  };

  return {
    settings,
    hasPersistedAssetCampuses,
    hasPersistedFiscalYearStart,
    terminology: settings.terminology,
    organizationId,
  };
}

/**
 * A write to one organization, carrying the organization it was made for. TanStack hands a pending
 * or offline-paused mutation the options of the latest render and calls mutationFn only when the
 * write runs, so a mutationFn that read the organization from its render would send a change made
 * for one church to whichever church is selected when it runs (#6736). Pinning the ids in the
 * variables when the write is made keeps it with the church it was typed for.
 */
interface OrgWrite<TValue> {
  /** The church the write goes to: the one the write hooks targeted when it was made. */
  organizationId: string | null;
  /**
   * The church whose values the change was typed against: the one whose values the page showed
   * for it when it was made. A support session on a church you don't belong to shows your own
   * church's name, settings, branding and logo, but that church's support email, members and
   * background-check vendor, so this differs by write, not by page.
   */
  editedFor: string | null;
  value: TValue;
}

type OrgWriteMutation<TData, TValue> = Omit<
  UseMutationResult<TData, Error, OrgWrite<TValue>>,
  "mutate" | "mutateAsync"
> & {
  mutate: (value: TValue, options?: MutateOptions<TData, Error, TValue>) => void;
  mutateAsync: (value: TValue, options?: MutateOptions<TData, Error, TValue>) => Promise<TData>;
};

/** A caller's per-call callbacks, which receive the value it passed rather than the pinned write. */
function valueCallbacks<TData, TValue>(
  options: MutateOptions<TData, Error, TValue> | undefined,
): MutateOptions<TData, Error, OrgWrite<TValue>> | undefined {
  if (!options) return undefined;
  const { onSuccess, onError, onSettled } = options;
  return {
    onSuccess: onSuccess && ((data, write, result, context) => onSuccess(data, write.value, result, context)),
    onError: onError && ((error, write, result, context) => onError(error, write.value, result, context)),
    onSettled: onSettled && ((data, error, write, result, context) => onSettled(data, error, write.value, result, context)),
  };
}

type LoadedOrganization = ReturnType<typeof useOrganization>["organizations"][number];

const ORGANIZATION_NOT_LOADED =
  "This church's details aren't loaded, so nothing was changed. Refresh the page and try again.";

const EDITED_FOR_ANOTHER_CHURCH =
  "These details belong to a different church from the one you're working in, so nothing was changed.";

/**
 * The church a write goes to. Refused when the change was typed against another church's values,
 * which it would otherwise copy into this one.
 */
function writeTarget<TValue>(write: OrgWrite<TValue>): string {
  if (!write.organizationId) throw new Error("No organization selected");
  if (write.editedFor !== write.organizationId) throw new Error(EDITED_FOR_ANOTHER_CHURCH);
  return write.organizationId;
}

/**
 * The record of the organization a write is for, by its id: never the organization of the render
 * the write runs in, which can be another church by then. Null when that church isn't loaded (for
 * example a support session on a church you don't belong to): a write built on what is stored
 * there is refused instead.
 */
function loadedOrganization(
  organizations: LoadedOrganization[] | undefined,
  organization: LoadedOrganization | null | undefined,
  organizationId: string,
): LoadedOrganization | null {
  const found = (organizations ?? []).find((org) => org.id === organizationId);
  if (found) return found;
  return organization?.id === organizationId ? organization : null;
}

/**
 * The mutation with `mutate(value)`/`mutateAsync(value)`, each pinned to the organization of the
 * call and to the church whose values it was typed against (see OrgWrite).
 */
function usePinnedOrgWrite<TData, TValue>(
  mutation: UseMutationResult<TData, Error, OrgWrite<TValue>>,
  organizationId: string | null,
  editedFor: string | null,
): OrgWriteMutation<TData, TValue> {
  const { mutate: mutateWrite, mutateAsync: mutateWriteAsync } = mutation;
  const mutate = useCallback(
    (value: TValue, options?: MutateOptions<TData, Error, TValue>) =>
      mutateWrite({ organizationId, editedFor, value }, valueCallbacks(options)),
    [mutateWrite, organizationId, editedFor],
  );
  const mutateAsync = useCallback(
    (value: TValue, options?: MutateOptions<TData, Error, TValue>) =>
      mutateWriteAsync({ organizationId, editedFor, value }, valueCallbacks(options)),
    [mutateWriteAsync, organizationId, editedFor],
  );
  return { ...mutation, mutate, mutateAsync };
}

/**
 * The guard for callers that bind a value to the organization it was built from (#7765):
 * `pinOrgWrite` here and `pinOrgSettings` below. A pinned value is refused, before any upload or
 * write, unless its organization is the one the write goes to (the organization pinned when the
 * write was made, above), so a value captured for organization A is never sent to B. A write made
 * for A that is still waiting when the user switches to B goes to A, as every write here does.
 * An unwrapped value behaves exactly as above.
 */
export interface PinnedOrgWrite<T> {
  readonly pinnedOrganizationId: string;
  readonly value: T;
}

export function pinOrgWrite<T>(pinnedOrganizationId: string, value: T): PinnedOrgWrite<T> {
  return { pinnedOrganizationId, value };
}

function resolveOrgWrite<T>(write: T | PinnedOrgWrite<T>, targetOrganizationId: string): T {
  const pinned =
    typeof write === "object" && write !== null && "pinnedOrganizationId" in write && "value" in write
      ? (write as PinnedOrgWrite<T>)
      : null;
  if (pinned === null) return write as T;
  if (pinned.pinnedOrganizationId !== targetOrganizationId) {
    throw new Error("The organization changed before this was saved. Nothing was changed.");
  }
  return pinned.value;
}

export function useUpdateOrgName() {
  const { organizationId, organization, refetch } = useOrganization();

  const mutation = useMutation({
    mutationFn: async (write: OrgWrite<string | PinnedOrgWrite<string>>) => {
      const targetOrgId = writeTarget(write);
      const name = resolveOrgWrite(write.value, targetOrgId);

      const { error } = await supabase
        .from("organizations")
        .update({ name, updated_at: new Date().toISOString() } as never)
        .eq("id", targetOrgId);

      if (error) throw error;

      await logAudit({
        tableName: "organizations",
        recordId: targetOrgId,
        action: "update_org_name",
        organizationId: targetOrgId,
        newData: { name },
      });
    },
    onSuccess: () => {
      refetch();
      toast.success("Organization name updated");
    },
    onError: (error) => {
      toast.error("Failed to update name", {
        description: error.message,
      });
    },
  });
  // The name field shows the church on screen's name.
  return usePinnedOrgWrite(mutation, organizationId, organization?.id ?? null);
}

/**
 * A settings write bound to the organization its values were built from (#7765). Values built
 * after an await (a fund's settings, once its row update resolves) can be handed to the hook
 * from a later render than the one they were built in. Wrapping them with `pinOrgSettings`
 * makes the hook refuse the write unless it goes to the organization named here (see
 * PinnedOrgWrite), so they are never merged into another organization's settings.
 */
export interface PinnedOrgSettings {
  readonly pinnedOrganizationId: string;
  /** A function is given that organization's settings as stored when the write runs (#7830). */
  readonly settings: Partial<OrgSettings> | ((current: OrgSettings) => Partial<OrgSettings>);
}

export function pinOrgSettings(
  pinnedOrganizationId: string,
  settings: PinnedOrgSettings["settings"],
): PinnedOrgSettings {
  return { pinnedOrganizationId, settings };
}

function isPinnedOrgSettings(value: Partial<OrgSettings> | PinnedOrgSettings): value is PinnedOrgSettings {
  return typeof (value as PinnedOrgSettings).pinnedOrganizationId === "string" && "settings" in value;
}

export function useUpdateOrgSettings() {
  const { organizationId, organization, organizations, refetch } = useOrganization();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const coordinator = getOrgSettingsSaveCoordinator(queryClient, user?.id ?? null);
  const reconcile = () => { void Promise.resolve().then(() => refetch()).catch(() => {}); };

  const mutation = useMutation({
    mutationFn: async (write: OrgWrite<Partial<OrgSettings> | PinnedOrgSettings>) => {
      const targetOrgId = writeTarget(write);
      if (isPinnedOrgSettings(write.value) && write.value.pinnedOrganizationId !== targetOrgId) {
        throw new Error("The organization changed before these settings were saved. Nothing was changed.");
      }
      const newSettings = isPinnedOrgSettings(write.value) ? write.value.settings : write.value;
      // Re-derive collection updates from the row read on every CAS attempt, including retries.
      const patch = typeof newSettings === "function"
        ? (current: Record<string, unknown>) => newSettings(parseChmsOrgSettings(current) as unknown as OrgSettings)
        : newSettings;
      if (!loadedOrganization(organizations, organization, targetOrgId)) throw new Error(ORGANIZATION_NOT_LOADED);
      const saved = await coordinator.enqueue(targetOrgId, typeof patch === "function" ? {} : patch, async (signal) => {
        // Functional collection edits have no speculative overlay: applying one to the confirmed
        // base while it is still pending would append it twice. Plain patches keep DEV's overlay.
        const result = await writeOrgSettingsPatch(targetOrgId, patch, signal);
        // Audit every confirmed commit, including a response arriving after the UI deadline.
        // Start it without holding the shared save queue or the memberships read-back open.
        const audit = Promise.resolve().then(() => logAudit({
          tableName: "organizations", recordId: targetOrgId, action: "update_org_settings", organizationId: targetOrgId,
          oldData: { settings: result.previous ?? null }, newData: { settings: result.settings as Record<string, unknown> },
        }));
        void audit.catch(() => {});
        if (signal.aborted) throw new Error("Settings save expired");
        // Retire reads that began before this commit before dropping its optimistic overlay.
        await queryClient.cancelQueries({ queryKey: ["organization-memberships", user?.id ?? null], exact: true });
        if (signal.aborted) throw new Error("Settings save expired");
        cacheConfirmedOrgSettings(queryClient, user?.id ?? null, targetOrgId, result.settings);
        return { ...result, audit };
      }, reconcile);
      await saved.audit;
      return saved.patch as Partial<OrgSettings>;
    },
    onSuccess: (_data, write) => {
      const savedFor = write.organizationId;
      const onScreen = readEffectiveOrganizationId() ?? organizationId;
      if (savedFor !== null && savedFor !== onScreen) {
        const name = loadedOrganization(organizations, organization, savedFor)?.name;
        toast.success(name ? `Settings updated for ${name}` : "Settings updated for the church you made them in");
        return;
      }
      toast.success("Settings updated");
    },
    onError: (error) => { toast.error("Failed to update settings", { description: error.message }); },
  });
  const pinned = usePinnedOrgWrite(mutation, organizationId, organization?.id ?? null);
  const { mutate: mutateWrite } = mutation;
  // A create resumed after a church switch must extend its own church's stored collections.
  const mutateForOrganization = useCallback(
    (targetOrganizationId: string, settings: PinnedOrgSettings["settings"]) =>
      mutateWrite({
        organizationId: targetOrganizationId,
        editedFor: targetOrganizationId,
        value: pinOrgSettings(targetOrganizationId, settings),
      }),
    [mutateWrite],
  );
  return { ...pinned, mutateForOrganization };
}

// org-assets public URLs look like
// `${SUPABASE_URL}/storage/v1/object/public/org-assets/${path}`. Recovering
// `path` from a previously-stored logo_url lets useUpdateOrgLogo clean up the
// object it's about to supersede without a separate DB column to track it.
function extractOrgAssetPath(publicUrl: string | null | undefined): string | null {
  if (!publicUrl) return null;
  const marker = "/object/public/org-assets/";
  const index = publicUrl.indexOf(marker);
  if (index === -1) return null;
  return publicUrl.slice(index + marker.length);
}

export function useUpdateOrgLogo() {
  const { organizationId: currentOrgId, organization, organizations, refetch } = useOrganization();

  const mutation = useMutation({
    mutationFn: async (write: OrgWrite<File | PinnedOrgWrite<File>>) => {
      const organizationId = writeTarget(write);
      // Refused before any upload when pinned to another organization.
      const file = resolveOrgWrite(write.value, organizationId);
      // The logo this upload supersedes is that organization's own, never another church's.
      const target = loadedOrganization(organizations, organization, organizationId);
      if (!target) throw new Error(ORGANIZATION_NOT_LOADED);

      const previousFilePath = extractOrgAssetPath(target.logo_url);

      const fileExt = (file.name.split(".").pop() || "png").toLowerCase();

      // Hash the content BEFORE picking the object path, so the path itself is
      // content-addressed instead of a stable path plus a mutable cache-buster.
      // A prior version of this fix kept the stable `logo.<ext>` path and
      // appended `?v=<hash>` to the URL — but that leaves a window where the
      // storage upload succeeds, the `organizations` update below fails (or a
      // second concurrent upload interleaves), and the stable object now holds
      // different bytes than the hash still recorded in `logo_url` claims.
      // Content-addressing makes that divergence structurally impossible: this
      // upload's object lives at its own path, so a failed org-record update
      // leaves the old logo's path (and hash) completely untouched.
      //
      // Copy into a same-realm Uint8Array before hashing: jsdom's
      // File.arrayBuffer() (used in this hook's own tests) returns an
      // ArrayBuffer from jsdom's VM context, which Node's SubtleCrypto.digest
      // rejects with "2nd argument is not instance of ArrayBuffer, Buffer,
      // TypedArray, or DataView" — confirmed reproducing under the exact Node
      // version (22.14.0) CI runs on, though not under a newer local Node
      // where the realm check happens to pass.
      const fileBytes = Uint8Array.from(new Uint8Array(await file.arrayBuffer()));
      const contentDigest = await crypto.subtle.digest("SHA-256", fileBytes);
      const contentHash = Array.from(new Uint8Array(contentDigest))
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("")
        .slice(0, 16);

      // `logo.<hash>.<ext>`, not `logo-<hash>.<ext>`: the storage RLS policies
      // gating both upload/update ("Org members can upload/update allowed
      // paths") and, critically, anonymous PUBLIC read ("Logos are public",
      // supabase/migrations/20260201162551_*.sql) match on the literal
      // substring `/logo.` — see
      // supabase/migrations/20260616120000_lockdown_org_assets_public_listing.sql,
      // which deliberately narrowed public storage access to exactly that
      // pattern. Keeping the dot immediately after "logo" preserves the match;
      // a hyphen would silently 404 the public bulletin header and branded
      // emails for every org.
      const filePath = `${organizationId}/uploads/logo.${contentHash}.${fileExt}`;

      // Upload to storage. upsert:true is safe here even though the path is
      // now content-derived: re-uploading identical bytes recomputes the same
      // hash and path, so it's a same-content no-op rather than a real upsert.
      const { error: uploadError } = await supabase.storage
        .from("org-assets")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // `org-assets` is a PUBLIC bucket and the "Logos are public" storage policy
      // keeps any `**/logo.*` object anonymously fetchable, so persist a permanent
      // public URL. A signed URL would expire after its TTL and 404 on public/
      // long-lived surfaces (the public bulletin header, branded emails, etc.).
      const { data: urlData } = supabase.storage.from("org-assets").getPublicUrl(filePath);
      const publicUrl = urlData.publicUrl;

      // Update organization with the permanent public URL
      const { error: updateError } = await supabase
        .from("organizations")
        .update({
          logo_url: publicUrl,
          updated_at: new Date().toISOString(),
        } as never)
        .eq("id", organizationId);

      if (updateError) {
        // Roll back the just-uploaded object so a failed `organizations`
        // update doesn't leave it permanently orphaned (and, since it lives
        // under `**/logo.*`, anonymously readable under "Logos are public")
        // with nothing in the DB ever pointing to it. Best-effort: the
        // original updateError is still what surfaces to the caller either
        // way.
        const { error: rollbackError } = await supabase.storage
          .from("org-assets")
          .remove([filePath]);
        if (rollbackError) {
          console.error("Failed to roll back orphaned logo upload", rollbackError);
        }
        throw updateError;
      }

      // The swap succeeded, so the previous logo object (if any, and if
      // distinct from this content-addressed upload) is now superseded and
      // unreferenced. Storage RLS has no automatic GC, so without this every
      // logo change permanently leaks another object under the bucket's
      // public-read policy. Best-effort: a cleanup failure (e.g. a non-admin
      // caller lacking the org-assets DELETE grant — "Org admins can delete
      // assets" in 20260618150000_wrap_storage_rls_auth_initplan.sql)
      // shouldn't undo the logo change that already committed.
      if (previousFilePath && previousFilePath !== filePath) {
        const { error: cleanupError } = await supabase.storage
          .from("org-assets")
          .remove([previousFilePath]);
        if (cleanupError) {
          console.error("Failed to remove superseded org logo", cleanupError);
        }
      }

      await logAudit({
        tableName: "organizations",
        recordId: organizationId,
        action: "update_org_logo",
        organizationId,
        metadata: { file_path: filePath },
      });

      return publicUrl;
    },
    onSuccess: () => {
      refetch();
      toast.success("Logo updated");
    },
    onError: (error) => {
      toast.error("Failed to upload logo", {
        description: error.message,
      });
    },
  });
  // The logo shown, and replaced, is the church on screen's.
  return usePinnedOrgWrite(mutation, currentOrgId, organization?.id ?? null);
}

export interface OrgBranding {
  accent_color?: string | null;
  footer_text?: string | null;
  statement_header_text?: string | null;
  statement_footer_text?: string | null;
}

export function useUpdateOrgBranding() {
  const { organizationId: currentOrgId, organization, refetch } = useOrganization();

  const mutation = useMutation({
    mutationFn: async (write: OrgWrite<OrgBranding | PinnedOrgWrite<OrgBranding>>) => {
      const organizationId = writeTarget(write);
      const branding = resolveOrgWrite(write.value, organizationId);

      const { error } = await supabase
        .from("organizations")
        .update({
          ...branding,
          updated_at: new Date().toISOString(),
        } as never)
        .eq("id", organizationId);

      if (error) throw error;

      await logAudit({
        tableName: "organizations",
        recordId: organizationId,
        action: "update_org_branding",
        organizationId,
        newData: branding as unknown as Record<string, unknown>,
      });
    },
    onSuccess: () => {
      refetch();
      toast.success("Branding updated");
    },
    onError: (error) => {
      toast.error("Failed to update branding", {
        description: error.message,
      });
    },
  });
  // The branding fields show the church on screen's branding.
  return usePinnedOrgWrite(mutation, currentOrgId, organization?.id ?? null);
}


/**
 * Background-check vendor selection lives in the typed `organizations
 * .bg_check_vendor_selection` column (NOT the `settings` jsonb), so it needs its
 * own read/write hooks. Historically this column was set only by seed data and had
 * no admin surface — W6 wires it up so an admin can choose the org's screening
 * vendor (see SettingsKidsCheckIn + the volunteer Background Checks deep-link).
 */
export type BgCheckVendorSelection = "ministrysafe" | "pmm" | "checkr" | "none";

export const BG_CHECK_VENDOR_OPTIONS: { value: BgCheckVendorSelection; label: string }[] = [
  { value: "none", label: "Not configured" },
  { value: "ministrysafe", label: "MinistrySafe" },
  { value: "pmm", label: "Protect My Ministry" },
  { value: "checkr", label: "Checkr" },
];

export function useBgCheckVendor() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["bg-check-vendor", organizationId],
    enabled: !!organizationId,
    queryFn: async (): Promise<BgCheckVendorSelection> => {
      if (!organizationId) return "none";

      const { data, error } = await supabase
        .from("organizations")
        .select("bg_check_vendor_selection")
        .eq("id", organizationId)
        .maybeSingle();

      if (error) throw error;

      return (
        (data as { bg_check_vendor_selection?: BgCheckVendorSelection } | null)
          ?.bg_check_vendor_selection ?? "none"
      );
    },
  });
}

export function useUpdateBgCheckVendor() {
  const { organizationId: currentOrgId } = useOrganization();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (write: OrgWrite<BgCheckVendorSelection>) => {
      const organizationId = writeTarget(write);
      const vendor = write.value;

      const { error } = await supabase
        .from("organizations")
        .update({
          bg_check_vendor_selection: vendor,
          updated_at: new Date().toISOString(),
        } as never)
        .eq("id", organizationId);

      if (error) throw error;

      await logAudit({
        tableName: "organizations",
        recordId: organizationId,
        action: "update_bg_check_vendor",
        organizationId,
        newData: { bg_check_vendor_selection: vendor },
      });

      return vendor;
    },
    onSuccess: (_data, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: ["bg-check-vendor", organizationId] });
      toast.success("Background-check vendor updated");
    },
    onError: (error) => {
      toast.error("Failed to update background-check vendor", {
        description: (error as Error).message,
      });
    },
  });
  // The vendor shown is useBgCheckVendor's, read for the church the writes go to.
  return usePinnedOrgWrite(mutation, currentOrgId, currentOrgId);
}

export interface OrgCommBrandProfile {
  id: string;
  name: string;
  support_contact_email: string | null;
}

export function useOrgCommBrandProfile() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["org-comm-brand-profile", organizationId],
    enabled: !!organizationId,
    queryFn: async (): Promise<OrgCommBrandProfile | null> => {
      if (!organizationId) return null;

      const { data, error } = await supabase
        .from("comm_brand_profiles")
        .select("id, name, support_contact_email")
        .eq("organization_id", organizationId)
        .eq("scope", "org")
        .eq("is_default", true)
        .maybeSingle();

      if (error) throw error;
      return data as OrgCommBrandProfile | null;
    },
  });
}

export function useUpdateOrgCommSupportEmail() {
  const { organization, organizations, organizationId: currentOrgId } = useOrganization();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (write: OrgWrite<string | null | PinnedOrgWrite<string | null>>) => {
      const organizationId = writeTarget(write);
      const supportContactEmail = resolveOrgWrite(write.value, organizationId);

      const email = supportContactEmail?.trim() || null;
      const { data: existing, error: lookupError } = await supabase
        .from("comm_brand_profiles")
        .select("id")
        .eq("organization_id", organizationId)
        .eq("scope", "org")
        .eq("is_default", true)
        .maybeSingle();

      if (lookupError) throw lookupError;

      if (existing?.id) {
        const { error } = await supabase
          .from("comm_brand_profiles")
          .update({
            support_contact_email: email,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existing.id);

        if (error) throw error;
      } else {
        // A new profile is named for its own church, never another one's.
        const target = loadedOrganization(organizations, organization, organizationId);
        if (!target) throw new Error(ORGANIZATION_NOT_LOADED);
        const { error } = await supabase.from("comm_brand_profiles").insert({
          scope: "org",
          organization_id: organizationId,
          name: target.name || "Organization",
          is_default: true,
          support_contact_email: email,
        });

        if (error) throw error;
      }

      await logAudit({
        tableName: "comm_brand_profiles",
        recordId: organizationId,
        action: "update_comm_support_email",
        organizationId,
        newData: { support_contact_email: email },
      });
    },
    onSuccess: (_data, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: ["org-comm-brand-profile", organizationId] });
      queryClient.invalidateQueries({ queryKey: ["comm-brand-profile", organizationId] });
      toast.success("Email support contact updated");
    },
    onError: (error) => {
      toast.error("Failed to update email support contact", {
        description: error.message,
      });
    },
  });
  // The email shown is useOrgCommBrandProfile's, read for the church the writes go to.
  return usePinnedOrgWrite(mutation, currentOrgId, currentOrgId);
}

export interface OrgMember {
  id: string;
  user_id: string;
  role: "owner" | "admin" | "member";
  created_at: string;
  email: string;
  name: string;
}

/** Profile row shape for org member display (profiles table may not be in active schema types). */
interface ProfileDisplayRow {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}

export function useOrgMembers() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["org-members", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];

      const { data: members, error } = await supabase
        .from("organization_members")
        .select("id, user_id, role, created_at")
        .eq("organization_id", organizationId);

      if (error && !isSupabaseUnavailableError(error)) {
        throw error;
      }

      // Fetch profile info for each member
      const membersWithProfiles: OrgMember[] = await Promise.all(
        (members || []).map(async (member) => {
          const { data: profile } = await supabase
            .from("profiles")
            .select("first_name, last_name, email")
            .eq("user_id", member.user_id)
            .maybeSingle();

          if (!profile) {
            return {
              id: member.id,
              user_id: member.user_id,
              role: member.role as "owner" | "admin" | "member",
              created_at: member.created_at,
              email: "No profile email on file",
              name: `Member (${member.user_id.slice(0, 8)})`,
            };
          }

          const p = profile as ProfileDisplayRow;
          const firstName = (p.first_name || "").trim();
          const lastName = (p.last_name || "").trim();
          const resolvedName = `${firstName} ${lastName}`.trim();

          return {
            id: member.id,
            user_id: member.user_id,
            role: member.role as "owner" | "admin" | "member",
            created_at: member.created_at,
            email: p.email || "No profile email on file",
            name: resolvedName || `Member (${member.user_id.slice(0, 8)})`,
          };
        })
      );

      return membersWithProfiles;
    },
    enabled: !!organizationId,
  });
}

export interface OrgMemberOnboarding {
  has_completed_setup: boolean;
  email_confirmed: boolean;
}

/**
 * Page size for the onboarding RPC, and a hard stop on paging.
 *
 * The size only has to be at or below PostgREST's `max_rows`; the loop detects
 * the end by a short page rather than by knowing the cap. The page ceiling
 * exists so a server that ignores `limit` cannot spin this forever — at this
 * size it covers a million members, far past any real org.
 */
const ONBOARDING_PAGE_SIZE = 1000;
const ONBOARDING_MAX_PAGES = 1000;

/**
 * Per-member account-setup state, keyed by user_id.
 *
 * A row in organization_members does NOT mean the person finished signing up —
 * invite-person writes that row when the invite is *sent*, not accepted. The
 * backing RPC is SECURITY DEFINER because the real signal lives in auth.users /
 * auth.identities, which the authenticated role can't read. (#4705)
 *
 * Note it reports "has a usable credential", NOT "has signed in":
 * auth.users.last_sign_in_at is stamped when a recovery link is merely clicked,
 * so it would flip to true for someone who abandoned setup and still can't log
 * in. See the migration for the full reasoning.
 *
 * Org owners/admins only; anyone else gets an empty set rather than an error.
 * Callers must therefore treat a missing entry as "unknown", never as
 * "stranded" — otherwise a non-admin viewer would see every colleague
 * mislabelled as incomplete.
 */
export function useOrgMemberOnboarding() {
  const { organizationId } = useOrganization();
  const { user } = useAuth();

  return useQuery({
    // Keyed by user id as well as org: this result is authorization-dependent
    // (admins get rows, everyone else gets none) and the QueryClient is a
    // module-level singleton that survives signOut. Without the user in the
    // key, a second account signing in to the same org in the same browser
    // could be served the previous admin's cached booleans.
    queryKey: ["org-member-onboarding", organizationId, user?.id],
    queryFn: async (): Promise<Record<string, OrgMemberOnboarding>> => {
      if (!organizationId) return {};

      // Paged, not one request. This RPC is set-returning, so PostgREST caps a
      // single response at its configured `max_rows`; one unpaginated call
      // silently truncates for a large org. Every omitted member then reads as
      // "unknown", which the invite-eligibility gate treats as invitable — so
      // an already-onboarded person past the cap stays selectable and hits the
      // very 400 that gate exists to prevent (Codex P2, PR #6091).
      const byUser: Record<string, OrgMemberOnboarding> = {};

      // Keyset, not offset. `get_org_member_onboarding_status` carries no
      // ORDER BY (20260727220000_org_member_onboarding_status.sql), so the row
      // order between two independent requests is not guaranteed to agree at
      // all — and even under a stable order, a membership removed before the
      // page boundary shifts the remainder and `.range()` skips a member. A
      // skipped member reads as "unknown", which fails open to invitable, so
      // the skip lands on exactly the bug this paging exists to fix (Codex P2,
      // PR #6091).
      //
      // organization_members is UNIQUE(organization_id, user_id), so within one
      // org's result set user_id is a unique key: ordering by it and resuming
      // strictly after the last one seen can neither skip nor repeat a row,
      // whatever changes underneath.
      let cursor: string | null = null;

      for (let page = 0; page < ONBOARDING_MAX_PAGES; page += 1) {
        let request = supabase
          .rpc("get_org_member_onboarding_status", { p_org_id: organizationId })
          .order("user_id", { ascending: true })
          .limit(ONBOARDING_PAGE_SIZE);
        if (cursor !== null) request = request.gt("user_id", cursor);

        const { data, error } = await request;

        if (error) {
          // Offline/degraded Supabase — and the deploy window before the
          // migration lands — shouldn't blank out the whole team list. Fall
          // back to "unknown" so rows render without the badge. Discarding the
          // pages already collected is deliberate: a partial map is the
          // truncation bug above wearing a different hat, and "unknown for
          // everyone" is the documented, fail-open answer.
          if (isSupabaseUnavailableError(error)) return {};
          throw error;
        }

        const rows = data ?? [];
        for (const row of rows) {
          byUser[row.user_id] = {
            has_completed_setup: row.has_completed_setup,
            email_confirmed: row.email_confirmed,
          };
        }

        // A short page is the last page. A full one means there may be more.
        if (rows.length < ONBOARDING_PAGE_SIZE) return byUser;
        cursor = rows[rows.length - 1].user_id;
      }

      return byUser;
    },
    enabled: !!organizationId && !!user?.id,
  });
}

export interface OrgInvite {
  id: string;
  email: string;
  role: "admin" | "member";
  created_at: string;
  expires_at: string;
}

export function useOrgInvites() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["org-invites", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .from("organization_invites")
        .select("id, email, role, created_at, expires_at")
        .eq("organization_id", organizationId)
        .is("accepted_at", null)
        .gt("expires_at", new Date().toISOString())
        .order("created_at", { ascending: false });

      if (error) throw error;

      return (data || []) as OrgInvite[];
    },
    enabled: !!organizationId,
  });
}

interface SendInviteRequest {
  email: string;
  role: "admin" | "member";
  inviterName: string;
  initialRoles?: AppRole[];
}

export function useSendInvite() {
  const { organizationId: currentOrgId, organization, organizations } = useOrganization();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (write: OrgWrite<SendInviteRequest>) => {
      const organizationId = writeTarget(write);
      const { email, role, inviterName, initialRoles = [] } = write.value;
      // The name of the church the invitation is for. For a church that isn't loaded (a support
      // session on a church you don't belong to) it is still the church on screen's name, as it
      // always was: this change pins where the invitation goes, not how it is worded (#6736).
      const organizationName = loadedOrganization(organizations, organization, organizationId)?.name ?? organization?.name;
      if (!organizationName) throw new Error("No organization selected");

      const { data, error } = await invokeEdgeFunction("send-invite", {
        body: {
          email,
          organizationId,
          organizationName,
          role,
          inviterName,
          initialRoles,
        },
        policy: "interactive",
      });

      if (error) throw error;
      if (data?.error) {
        throw new Error(getEdgeFunctionErrorMessage(data.error, "Failed to send invitation"));
      }

      return data as {
        resentAccountSetupLink?: boolean;
        requestedOrgRoleNotApplied?: boolean;
        existingOrgRole?: string;
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["org-invites"] });
      queryClient.invalidateQueries({ queryKey: ["org-member-onboarding"] });

      if (!data?.resentAccountSetupLink) {
        toast.success("Invitation sent");
        return;
      }

      // Not "never signed in": clicking an earlier recovery link already issues
      // a session, so they may well have signed in and simply not chosen a
      // password. Describe the unfinished setup, not the sign-in history.
      toast.success("Setup link sent", {
        description: data.requestedOrgRoleNotApplied
          ? `They were already added but never finished setting up their account, so we emailed them a link. Their access level is unchanged (${data.existingOrgRole ?? "member"}) — use Change Access to adjust it.`
          : "They were already added but never finished setting up their account, so we emailed them a link to finish.",
      });
    },
    onError: (error) => {
      toast.error("Failed to send invitation", {
        description: error.message,
      });
    },
  });
  // The team list it is sent from is useOrgMembers/useOrgInvites', read for the church the writes go to.
  return usePinnedOrgWrite(mutation, currentOrgId, currentOrgId);
}

export function useCancelInvite() {
  const queryClient = useQueryClient();
  const { organizationId: currentOrgId } = useOrganization();

  const mutation = useMutation({
    mutationFn: async (write: OrgWrite<string>) => {
      const organizationId = writeTarget(write);
      const inviteId = write.value;

      const { error } = await supabase
        .from("organization_invites")
        .delete()
        .eq("id", inviteId)
        .eq("organization_id", organizationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-invites"] });
      toast.success("Invitation cancelled");
    },
    onError: (error) => {
      toast.error("Failed to cancel invitation", {
        description: error.message,
      });
    },
  });
  // The invitation list is useOrgInvites', read for the church the writes go to.
  return usePinnedOrgWrite(mutation, currentOrgId, currentOrgId);
}

export function useResetMemberPassword() {
  const { organizationId: currentOrgId } = useOrganization();

  const mutation = useMutation({
    mutationFn: async (write: OrgWrite<{ targetUserId: string }>) => {
      const organizationId = writeTarget(write);
      const { targetUserId } = write.value;

      const { data, error } = await invokeEdgeFunction("org-reset-password", {
        body: {
          organizationId,
          targetUserId,
        },
        policy: "interactive",
      });

      if (error) throw error;
      if (data?.error) {
        throw new Error(getEdgeFunctionErrorMessage(data.error, "Failed to send password reset"));
      }

      return data as { mode?: "account_setup" | "password_reset" };
    },
    onSuccess: (data) => {
      toast.success(
        data?.mode === "account_setup"
          ? "Setup link sent"
          : "Password reset email sent",
      );
    },
    onError: (error) => {
      toast.error("Failed to send password reset", {
        description: error.message,
      });
    },
  });
  // The person it is sent for is listed for the church the writes go to.
  return usePinnedOrgWrite(mutation, currentOrgId, currentOrgId);
}
